import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FaBook, FaPlus, FaEdit, FaSave, FaImage, FaArrowLeft, FaUsers } from 'react-icons/fa';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import BackButton from "../components/BackButton";
import CustomModal from '../components/CustomModal';
import CharacterManager from '../components/CharacterManager';
import SceneCreator from '../components/SceneCreator';
import ArtworkTagger from '../components/ArtworkTagger';
import ArtworkInfoModal from '../components/ArtworkInfoModal';
import RefineArtworkModal from '../components/RefineArtworkModal';
import CharacterTagOverlay from '../components/CharacterTagOverlay';
import ArtworkUploadHandler from '../components/ArtworkUploadHandler';
import ArtworkDisplay from '../components/ArtworkDisplay';
import SceneManager from '../components/SceneManager';
import { API_ENDPOINTS, backendConfig } from '../config/backendConfig';
import GenerateArtworkModal from '../components/GenerateArtworkModal';
import { saveStoryActivity } from '../utils/activityService';
import { getCache, setCache, createCacheKey, clearCache } from '../utils/cache';
import { enqueueAndPoll } from '../utils/jobPolling';

console.log('StoryCreator file loaded');

export default function StoryCreator() {
  console.log('StoryCreator mounted');
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  // State for story fields
  const [storyId, setStoryId] = useState(id || null);
  const [showGenerateArtworkModal, setShowGenerateArtworkModal] = useState(false);
  const [storyTitle, setStoryTitle] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [bookCover, setBookCover] = useState('');
  const [about, setAbout] = useState('');
  const [brief, setBrief] = useState('');
  const [scenes, setScenes] = useState([]);
  const [originalArtwork, setOriginalArtwork] = useState('');
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [currentScene, setCurrentScene] = useState(null);
  const [isEditingScene, setIsEditingScene] = useState(false);
  const [showCover, setShowCover] = useState(true);
  const [showSceneEditor, setShowSceneEditor] = useState(false);
  const [currentSceneContent, setCurrentSceneContent] = useState('');
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null });
  const [currentUser, setCurrentUser] = useState(null);
  
  // New state for two-section layout (scene 2 onwards)
  const [sceneDescription, setSceneDescription] = useState(''); // Section 1: Story continuity
  const [artworkDetails, setArtworkDetails] = useState(''); // Section 2: Image generation details
  const [generatingArtwork, setGeneratingArtwork] = useState(false); // Loading state for artwork generation
  const [artworkHistory, setArtworkHistory] = useState({}); // Store previous artwork versions for each scene

  // Edit artwork functionality state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInstructions, setEditInstructions] = useState('');
  const [editingArtwork, setEditingArtwork] = useState(false);
  const [currentImageForEdit, setCurrentImageForEdit] = useState('');
  const [imageLoadErrors, setImageLoadErrors] = useState({}); // Track image load errors

  // Character management state
  const [characters, setCharacters] = useState([]);
  const [showCharacterManager, setShowCharacterManager] = useState(false);
  const [showSceneCreator, setShowSceneCreator] = useState(false);
  const [currentSceneNumber, setCurrentSceneNumber] = useState(1);

  // --- Add missing state for artwork info modal and first scene flow ---
  const [showArtworkInfoModal, setShowArtworkInfoModal] = useState(false);
  const [artworkType, setArtworkType] = useState('');
  const [artworkPeople, setArtworkPeople] = useState({ name: '', age: '', gender: '' });
  const [artworkObject, setArtworkObject] = useState({ name: '', brief: '' });
  const [artworkWorld, setArtworkWorld] = useState({ reason: '' });
  const [firstSceneGenerated, setFirstSceneGenerated] = useState(false);
  const [firstSceneLoading, setFirstSceneLoading] = useState(false);
  const [firstSceneError, setFirstSceneError] = useState(null);

  // --- Add new state for multiple characters and scene action ---
  const [artworkCharacters, setArtworkCharacters] = useState([
    { name: '', age: '', gender: '', appearance: '' }
  ]);
  const [sceneAction, setSceneAction] = useState('');

  // --- Add state for ArtworkTagger ---
  const [showArtworkTagger, setShowArtworkTagger] = useState(false);
  const [taggedCharacters, setTaggedCharacters] = useState([]);
  const [showCharacterTags, setShowCharacterTags] = useState(true); // Global toggle for character tags

  // --- Add state for Refine Artwork functionality ---
  const [showRefineArtworkModal, setShowRefineArtworkModal] = useState(false);
  const [currentArtworkForRefinement, setCurrentArtworkForRefinement] = useState('');
  const [currentTaggedCharactersForRefinement, setCurrentTaggedCharactersForRefinement] = useState([]);

  // Show new story flow if no storyId, no scenes, or only a placeholder scene
  const shouldShowNewStoryFlow = (() => {
    const result = (!storyId) ||
    (scenes.length === 0) ||
    (scenes.length === 1 && (!scenes[0]?.description || !scenes[0]?.imageUrl));
    
    console.log('=== SHOULD SHOW NEW STORY FLOW ===');
    console.log('storyId:', storyId);
    console.log('scenes.length:', scenes.length);
    console.log('scenes[0]?.description:', scenes[0]?.description);
    console.log('scenes[0]?.imageUrl:', scenes[0]?.imageUrl);
    console.log('firstSceneGenerated:', firstSceneGenerated);
    console.log('result:', result);
    
    // If first scene has been generated, don't show new story flow
    if (firstSceneGenerated && scenes.length > 0) {
      console.log('First scene generated, showing story content instead');
      return false;
    }
    
    return result;
  })();

  // Initialize ArtworkUploadHandler
  const artworkHandler = ArtworkUploadHandler({
    currentSceneIndex,
    scenes,
    setScenes,
    setCurrentScene,
    storyId,
    originalArtwork,
    setOriginalArtwork,
    setModal,
    setError
  });

  // Fix handleNextScene to always increment currentSceneIndex and create a new scene
  const handleNextScene = () => {
    const newScene = {
      title: `Scene ${scenes.length + 1}`,
      description: '',
      imageUrl: '',
      characters: [],
      setting: '',
      mood: '',
      artworkDetails: ''
    };
    const updatedScenes = [...scenes, newScene];
    setScenes(updatedScenes);
    setCurrentSceneIndex(updatedScenes.length - 1); // Always set to the new scene's index
    setCurrentSceneContent('');
    setCurrentScene(newScene);
      setSceneDescription('');
      setArtworkDetails('');
  };

  // Save story (create or update) - moved before SceneManager initialization
  const handleSaveStory = async () => {
    if (!currentUser) {
      setModal({
        isOpen: true,
        title: "🔐 Please Sign In",
        message: "You need to be signed in to save your story. Let's get you logged in first! ✨",
        type: 'warning'
      });
      return;
    }

    const story = {
      userId: currentUser.uid, // Use the authenticated user's ID
      title: storyTitle,
      author: authorName,
      coverUrl: bookCover,
      status: 'draft',
      scenes,
      about: about,
      brief: brief,
    };
    try {
      let res;
      if (id) {
        res = await fetch(`${API_ENDPOINTS.backendBase}/api/stories/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(story),
        });
      } else {
        res = await fetch(`${API_ENDPOINTS.backendBase}/api/stories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(story),
        });
      }
      if (!res.ok) throw new Error('Failed to save story');
      const data = await res.json();
      
      setModal({
        isOpen: true,
        title: "Story Saved Successfully!",
        message: "Your story has been saved to My Art Stories. Keep creating!",
        type: 'save'
      });
      
      // Save story activity to dashboard only when published
      try {
        await saveStoryActivity({
          originalImageUrl: originalArtwork,
          storyTitle: storyTitle,
          storyDescription: about || brief,
          characters: characters,
          scenes: scenes,
          storyId: data.id || id,
          status: 'draft'
        }, false); // Not published yet, just saved as draft
        console.log('✅ Story activity saved to dashboard (draft)');
        
        // Clear cache to force refresh (user's own activities and stories)
        if (currentUser) {
          const activitiesCacheKey = createCacheKey('userActivities', { userId: currentUser.uid }, currentUser.uid);
          const storyCacheKey = createCacheKey('story', { id: data.id || id }, currentUser.uid);
          clearCache(activitiesCacheKey);
          clearCache(storyCacheKey);
          console.log('✅ [STORY CREATOR] Cache cleared after save');
        }
      } catch (activityError) {
        console.warn('⚠️ Failed to save story activity:', activityError);
      }
      
      // Navigate after modal is closed
      setTimeout(() => {
      navigate(`/view-book/${data.id || id}`);
      }, 3000);
    } catch (err) {
      setModal({
        isOpen: true,
        title: "Oops! Something went wrong",
        message: "Don't worry, little author! Let's try saving your story again. Sometimes magic takes a moment to work! ✨",
        type: 'error'
      });
    }
  };

  // Initialize SceneManager
  const sceneManager = SceneManager({
    scenes,
    currentSceneIndex,
    setCurrentSceneIndex,
    setCurrentScene,
    setCurrentSceneContent,
    setSceneDescription,
    setArtworkDetails,
    setShowSceneCreator,
    setCurrentSceneNumber,
    handleNextScene,
    handleSaveStory,
    storyTitle,
    setStoryTitle,
    currentSceneContent,
    setCurrentSceneContent,
    onTellAtmosphereAboutArtwork: () => {
      console.log('=== TELL ATMOSPHERE BUTTON CLICKED FROM SCENE MANAGER ===');
      console.log('showArtworkInfoModal before:', showArtworkInfoModal);
      console.log('originalArtwork:', originalArtwork);
      console.log('artworkHandler.getFullImageUrl(originalArtwork):', artworkHandler.getFullImageUrl(originalArtwork));
      setShowArtworkInfoModal(true);
      console.log('showArtworkInfoModal after setState:', true);
    }
  });

  // Character management functions
  const loadCharacters = async () => {
    if (!storyId) return;
    
    // Check cache first
    const cacheKey = createCacheKey('storyCharacters', { storyId });
    const cached = getCache(cacheKey);
    if (cached) {
      console.log('✅ [STORY] Using cached story characters:', cached.length);
      setCharacters(cached);
      return;
    }
    
    try {
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/story/${storyId}`);
      if (response.ok) {
        const data = await response.json();
        const charactersData = data.characters || [];
        
        // Cache the results (5 min cache)
        setCache(cacheKey, charactersData, 5 * 60 * 1000);
        console.log('✅ [STORY] Story characters cached');
        
        setCharacters(charactersData);
      }
    } catch (error) {
      console.error('Error loading characters:', error);
    }
  };

  const handleCharacterUpdate = (updatedCharacters) => {
    setCharacters(updatedCharacters);
  };

  const handleSceneGenerated = (sceneData) => {
    const newScene = {
      title: sceneData.title,
      description: sceneData.description,
      imageUrl: sceneData.imageUrl,
      characters: sceneData.characters,
      setting: sceneData.setting,
      action: sceneData.action,
      additionalDetails: sceneData.additionalDetails
    };

    const updatedScenes = [...scenes];
    if (currentSceneIndex < updatedScenes.length) {
      // Update existing scene
      updatedScenes[currentSceneIndex] = newScene;
    } else {
      // Add new scene
      updatedScenes.push(newScene);
    }
    
    setScenes(updatedScenes);
    setCurrentScene(newScene);
    setShowSceneCreator(false);
    
    setModal({
      isOpen: true,
      title: "🎭 Scene Created Successfully!",
      message: "Fantastic! Your scene has been generated with all the characters and actions you specified! ✨",
      type: 'success'
    });
  };

  // Load characters when storyId changes
  useEffect(() => {
    if (storyId) {
      loadCharacters();
    }
  }, [storyId]);


  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        // Redirect to login if not authenticated
        navigate('/login', { state: { from: '/story-creator' } });
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Monitor scene changes for debugging
  useEffect(() => {
    console.log('=== SCENE STATE CHANGED ===');
    console.log('Scenes array:', scenes);
    console.log('Current scene index:', currentSceneIndex);
    console.log('Current scene:', scenes[currentSceneIndex]);
    console.log('Total scenes:', scenes.length);
  }, [scenes, currentSceneIndex]);

  // Monitor showArtworkTagger state for debugging
  useEffect(() => {
    console.log('=== SHOW ARTWORK TAGGER STATE CHANGED ===');
    console.log('showArtworkTagger:', showArtworkTagger);
  }, [showArtworkTagger]);

  // Fetch story if editing (id present)
  useEffect(() => {
    console.log('useEffect: fetchStory called, id:', id);
    async function fetchStory() {
      if (!id) return;
      
      // Check cache first
      const cacheKey = createCacheKey('story', { storyId: id });
      const cached = getCache(cacheKey);
      if (cached) {
        console.log('✅ [STORY] Using cached story data');
        setStoryId(cached._id || cached.id || id);
        setStoryTitle(cached.title || '');
        setAuthorName(cached.author || '');
        setBookCover(cached.coverUrl || cached.imageUrl || cached.originalArtwork || '');
        setAbout(cached.about || '');
        setBrief(cached.brief || '');
        const scenesWithConsistentTitles = (cached.scenes || []).map((scene, index) => ({
          ...scene,
          title: `Scene ${index + 1}`
        }));
        setScenes(scenesWithConsistentTitles);
        setOriginalArtwork(
          cached.originalArtwork ||
          (Array.isArray(cached.scenes) && cached.scenes[0]?.imageUrl) ||
          (Array.isArray(cached.scenes) && cached.scenes[0]?.coverUrl) ||
          ''
        );
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const res = await fetch(`${API_ENDPOINTS.backendBase}/api/stories/${id}`);
        console.log('fetchStory response:', res);
        if (!res.ok) throw new Error('Failed to fetch story');
        const data = await res.json();
        console.log('Fetched story data:', data);
        
        // Cache the story data (10 min cache - stories don't change often)
        setCache(cacheKey, data, 10 * 60 * 1000);
        console.log('✅ [STORY] Story data cached');
        
        setStoryId(data._id || data.id || id);
        setStoryTitle(data.title || '');
        setAuthorName(data.author || '');
        setBookCover(data.coverUrl || data.imageUrl || data.originalArtwork || '');
        setAbout(data.about || '');
        setBrief(data.brief || '');
        // Ensure consistent scene titles when loading existing stories
        const scenesWithConsistentTitles = (data.scenes || []).map((scene, index) => ({
          ...scene,
          title: `Scene ${index + 1}`
        }));
        setScenes(scenesWithConsistentTitles);
        setOriginalArtwork(
          data.originalArtwork ||
          (Array.isArray(data.scenes) && data.scenes[0]?.imageUrl) ||
          (Array.isArray(data.scenes) && data.scenes[0]?.coverUrl) ||
          ''
        );
      } catch (err) {
        setError(err.message);
        console.log('fetchStory error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchStory();
  }, [id]);

  // If not editing, pre-populate from location.state if available
  useEffect(() => {
    console.log('useEffect: pre-populate from location.state, id:', id, 'location.state:', location.state);
    if (id) return; // skip if editing
    const state = location.state || {};
    setStoryId(state.id || null);
    setStoryTitle(state.title || state.storyTitle || '');
    setAuthorName(state.author || state.authorName || '');
    setBookCover(state.coverUrl || state.imageUrl || state.originalArtwork || '');
    setAbout(state.about || '');
    setBrief(state.brief || '');
    
    // Only set scenes if a generatedScene is present (from "What's Your Story" feature)
    if (state.generatedScene) {
      console.log('Found generatedScene in state:', state.generatedScene);
      // Ensure consistent title for generated scene
      const sceneWithConsistentTitle = {
        ...state.generatedScene,
        title: 'Scene 1'
      };
      setScenes([sceneWithConsistentTitle]);
      setCurrentScene(sceneWithConsistentTitle);
      setCurrentSceneContent(sceneWithConsistentTitle.description || '');
      // If the generated scene has an imageUrl, use it as the original artwork
      if (state.generatedScene.imageUrl && !state.originalArtwork && !state.imageUrl) {
        setOriginalArtwork(state.generatedScene.imageUrl);
      }
    } else {
      // Do NOT set any scenes for new stories (no placeholder)
      setScenes([]);
      console.log('DEBUG: No generatedScene, setting scenes to []');
    }
    
    setOriginalArtwork(state.originalArtwork || state.imageUrl || state.coverUrl || '');
    console.log('DEBUG: After pre-populate, scenes:', scenes);
  }, [location.state, id]);

  const generateFirstScene = async () => {
    // Show SceneCreator component for first scene setup
    setCurrentSceneNumber(1);
    setShowSceneCreator(true);
  };

  const handleFirstSceneSetup = async () => {
    // This function is no longer needed - using SceneCreator component instead
    console.log('handleFirstSceneSetup is deprecated - using SceneCreator component');
  };

  const addNewScene = async () => {
    setShowSceneEditor(true);
    setCurrentSceneContent('');
    setIsEditingScene(true);
  };

  const saveScene = async () => {
    if (!currentSceneContent.trim()) {
      setModal({
        isOpen: true,
        title: "✍️ Write Your Scene",
        message: "Please write something wonderful for your scene before saving! Your imagination is waiting to be shared! ✨",
        type: 'warning'
      });
      return;
    }

    setLoading(true);
    try {
      // For scenes after the first one, use the previous scene's image as input
      let imageUrl = '';
      
      if (scenes.length > 0) {
        // Get the previous scene's image as input
        const previousSceneImage = scenes[scenes.length - 1]?.imageUrl;
        
        if (previousSceneImage) {
          // Generate image using previous scene's image + user's description
          // Use background worker with polling
          const imageData = await enqueueAndPoll(
            API_ENDPOINTS.generateStoryImageFirebase || API_ENDPOINTS.generateStoryImage,
            {
              previousImageUrl: getFullImageUrl(previousSceneImage),
              scenePrompt: currentSceneContent,
              characterIds: taggedCharacters.map(char => char.id), // Pass character IDs for image combination
            },
            {
              interval: 2000, // Poll every 2 seconds
              maxAttempts: 150, // 5 minutes max
              baseUrl: 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api'
            }
          );
          imageUrl = imageData.generatedImageUrl || imageData.imageUrl;
        }
      }

      // Create the new scene object
      const newScene = {
        title: `Scene ${scenes.length + 1}`,
        description: currentSceneContent,
        imageUrl: imageUrl,
        characters: [],
        setting: '',
        mood: ''
      };

      setScenes([...scenes, newScene]);
      setShowSceneEditor(false);
      setIsEditingScene(false);
      setCurrentSceneContent('');
      
      setModal({
        isOpen: true,
        title: "✨ Scene Saved Successfully!",
        message: "Amazing! Your scene has been saved and a beautiful illustration has been created for it! 🎨\n\nYour story is growing more magical with every scene! 📚",
        type: 'success'
      });
    } catch (error) {
      setModal({
        isOpen: true,
        title: "Oops! Something went wrong",
        message: "Don't worry, little storyteller! Let's try saving your scene again. Sometimes magic takes a moment to work! ✨",
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateBookCover = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/story/generate-cover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl: originalArtwork,
          title: storyTitle,
          scenes
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate book cover');
      }

      const data = await response.json();
      setBookCover(data.coverUrl);
      setShowCover(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Redirect if imageUrl is missing
  useEffect(() => {
    if (loading) return; // Don't check until loading is done

    // Only check after state is set
    const hasArtwork =
      !!originalArtwork ||
      !!bookCover ||
      (Array.isArray(scenes) && scenes.some(scene => !!scene.imageUrl || !!scene.coverUrl));

    if (!hasArtwork) {
      setError('No artwork found. Please start your story from Magic Studio or select an artwork.');
      // Do NOT auto-redirect. Let the user decide.
    } else {
      setError(null); // Clear any previous error
    }
  }, [originalArtwork, bookCover, scenes, loading]);

  // Switch to a different scene tab
  const handleSceneTabClick = (idx) => {
    setCurrentSceneIndex(idx);
    setCurrentSceneContent(scenes[idx].description);
    setCurrentScene(scenes[idx]);
    
    // Handle two-section layout for scene 2 onwards
    if (idx > 0) {
      // For scene 2 onwards, populate both sections
      setSceneDescription(scenes[idx].description || '');
      setArtworkDetails(scenes[idx].artworkDetails || '');
    } else {
      // For scene 1, use single content area
      setSceneDescription('');
      setArtworkDetails('');
    }
  };

  // Save/update the current scene
  const handleSaveCurrentScene = () => {
    if (currentSceneIndex === 0) {
      // Scene 1: Use single content area
    if (!currentSceneContent.trim()) {
      setModal({
        isOpen: true,
        title: "✍️ Write Your Scene",
        message: "Please write something wonderful for your scene before saving! Your imagination is waiting to be shared! ✨",
        type: 'warning'
      });
      return;
    }
    const updatedScenes = [...scenes];
    if (updatedScenes[currentSceneIndex]) {
    updatedScenes[currentSceneIndex] = {
      ...updatedScenes[currentSceneIndex],
      description: currentSceneContent
    };
    setScenes(updatedScenes);
    setError(null);
      
      setModal({
        isOpen: true,
        title: "✨ Scene Updated!",
        message: "Perfect! Your scene has been updated with your amazing writing! ✨\n\nKeep creating magic, little storyteller! 📝",
        type: 'save'
      });
    }
    } else {
      // Scene 2 onwards: Use single content area (scene description)
      if (!sceneDescription.trim()) {
        setModal({
          isOpen: true,
          title: "✍️ Write Your Scene",
          message: "Please write something wonderful for your scene description before saving! Your imagination is waiting to be shared! ✨",
          type: 'warning'
        });
        return;
      }
      
      const updatedScenes = [...scenes];
      if (updatedScenes[currentSceneIndex]) {
        updatedScenes[currentSceneIndex] = {
          ...updatedScenes[currentSceneIndex],
          description: sceneDescription
        };
        setScenes(updatedScenes);
        setError(null);
        
        setModal({
          isOpen: true,
          title: "✨ Scene Updated!",
          message: "Perfect! Your scene has been updated with your amazing writing! ✨\n\nKeep creating magic, little storyteller! 📝",
          type: 'save'
        });
      }
    }
  };

  // Add this helper for popup
  const showPopup = (msg) => {
    setModal({
      isOpen: true,
      title: "💡 Story Tip",
      message: msg,
      type: 'info'
    });
  };

  // Undo artwork function
  const undoArtwork = () => {
    const sceneKey = `scene_${currentSceneIndex}`;
    const previousArtwork = artworkHistory[sceneKey];
    
    if (previousArtwork) {
      const updatedScenes = [...scenes];
      if (updatedScenes[currentSceneIndex]) {
        updatedScenes[currentSceneIndex].imageUrl = previousArtwork;
        setScenes(updatedScenes);
        setCurrentScene(updatedScenes[currentSceneIndex]);
        
        // Remove from history after using it
        const updatedHistory = { ...artworkHistory };
        delete updatedHistory[sceneKey];
        setArtworkHistory(updatedHistory);
        
        setModal({
          isOpen: true,
          title: "↩️ Artwork Restored!",
          message: "Your previous artwork has been restored! You can always generate a new version if you'd like to try again. ✨",
          type: 'success'
        });
      }
    } else {
      setModal({
        isOpen: true,
        title: "No Previous Artwork",
        message: "There's no previous artwork to restore for this scene. Generate some artwork first! ✨",
        type: 'info'
      });
    }
  };

  // Test API connectivity
  const testAPIConnectivity = async () => {
    try {
      console.log('Testing API connectivity...');
      const response = await fetch(`${API_ENDPOINTS.backendBase}/health`);
      console.log('Health check response:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Health check data:', data);
        return true;
      }
      return false;
    } catch (error) {
      console.error('API connectivity test failed:', error);
      return false;
    }
  };

  // Edit artwork function
  const editArtwork = async () => {
    if (!editInstructions.trim()) {
      setModal({
        isOpen: true,
        title: "✏️ Edit Instructions Required",
        message: "Please describe what you'd like to change in the artwork! ✨",
        type: 'warning'
      });
      return;
    }

    if (!currentImageForEdit) {
      setModal({
        isOpen: true,
        title: "🖼️ No Image to Edit",
        message: "There's no artwork to edit. Please generate artwork first! ✨",
        type: 'warning'
      });
      return;
    }

    // Validate the image URL - accept local URLs too
    if (!currentImageForEdit.startsWith('http') && !currentImageForEdit.startsWith('data:') && !currentImageForEdit.startsWith('/uploads')) {
      setModal({
        isOpen: true,
        title: "🖼️ Invalid Image URL",
        message: "The image URL is not valid. Please try generating artwork again! ✨",
        type: 'warning'
      });
      return;
    }

    setEditingArtwork(true);
    setError(null);

    try {
      console.log('=== EDITING ARTWORK ===');
      console.log('Current image URL:', currentImageForEdit);
      console.log('Edit instructions:', editInstructions);
      console.log('API endpoint:', API_ENDPOINTS.editArtwork);

      // Test API connectivity first
      const isAPIAvailable = await testAPIConnectivity();
      if (!isAPIAvailable) {
        throw new Error('Backend API is not accessible. Please check if the backend server is running.');
      }

      // Save current artwork to history before editing
      const currentArtwork = scenes[currentSceneIndex]?.imageUrl;
      if (currentArtwork) {
        const sceneKey = `scene_${currentSceneIndex}`;
        setArtworkHistory(prev => ({
          ...prev,
          [sceneKey]: currentArtwork
        }));
        console.log('Saved current artwork to history:', currentArtwork);
      }

      // Convert local URL to full URL for the API call
      const fullImageUrl = getFullImageUrl(currentImageForEdit);

      const requestBody = {
        generatedImageUrl: fullImageUrl,
        editInstructions: editInstructions
      };

      console.log('Edit request body:', requestBody);

      const response = await fetch(API_ENDPOINTS.editArtwork, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('Edit response status:', response.status);
      console.log('Edit response ok:', response.ok);

      if (!response.ok) {
        let backendError = 'Failed to edit artwork.';
        try {
          const errData = await response.json();
          backendError = errData.error || backendError;
          console.log('Backend error data:', errData);
          
          // Handle specific error cases
          if (errData.details && errData.details.includes('download')) {
            backendError = 'Unable to access the image for editing. Please try generating new artwork first.';
          }
        } catch {}
        throw new Error(backendError);
      }

      const data = await response.json();
      console.log('Edit response data:', data);

      if (!data.generatedImageUrl) {
        throw new Error('No edited image URL received from backend');
      }

      // Update the current scene's imageUrl
      const updatedScenes = [...scenes];
      if (updatedScenes[currentSceneIndex]) {
        updatedScenes[currentSceneIndex].imageUrl = data.generatedImageUrl;
        setScenes(updatedScenes);
        setCurrentScene(updatedScenes[currentSceneIndex]);
        console.log('Updated scene with edited artwork:', data.generatedImageUrl);
      }

      // Close edit modal and show success message
      setShowEditModal(false);
      setEditInstructions('');
      setCurrentImageForEdit('');
      
      setModal({
        isOpen: true,
        title: "✨ Artwork Edited!",
        message: "Your artwork has been successfully edited! The changes have been applied to your scene. ✨",
        type: 'success'
      });

    } catch (error) {
      console.error('Edit artwork error:', error);
      setError(error.message);
      setModal({
        isOpen: true,
        title: "❌ Edit Failed",
        message: `Failed to edit artwork: ${error.message}`,
        type: 'error'
      });
    } finally {
      setEditingArtwork(false);
    }
  };

  // Open edit modal function
  const openEditModal = (imageUrl) => {
    setCurrentImageForEdit(imageUrl);
    setEditInstructions('');
    setShowEditModal(true);
  };

  // Open refine artwork modal function
  const openRefineArtworkModal = (imageUrl, taggedChars = []) => {
    console.log('=== OPENING REFINE ARTWORK MODAL ===');
    console.log('imageUrl:', imageUrl);
    console.log('imageUrl type:', typeof imageUrl);
    console.log('imageUrl length:', imageUrl?.length);
    console.log('taggedChars:', taggedChars);
    console.log('taggedChars length:', taggedChars?.length);
    console.log('showRefineArtworkModal before:', showRefineArtworkModal);
    console.log('currentUser:', currentUser);
    
    setCurrentArtworkForRefinement(imageUrl);
    setCurrentTaggedCharactersForRefinement(taggedChars);
    setShowRefineArtworkModal(true);
    
    console.log('showRefineArtworkModal after setState:', true);
    console.log('currentArtworkForRefinement set to:', imageUrl);
    console.log('currentTaggedCharactersForRefinement set to:', taggedChars);
  };

  // Handle artwork refinement
  const handleArtworkRefined = (refinedImageUrl) => {
    // Update the current scene's artwork
    const updatedScenes = [...scenes];
    if (updatedScenes[currentSceneIndex]) {
      updatedScenes[currentSceneIndex].imageUrl = refinedImageUrl;
      setScenes(updatedScenes);
      
      // Clear the image load error for this scene
      setImageLoadErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[currentSceneIndex];
        return newErrors;
      });

      // Force a re-render by updating current scene
      setCurrentScene(updatedScenes[currentSceneIndex]);
    }
  };

  // Convert local URLs to full backend URLs
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return imageUrl;
    
    // If it's already a full URL, return as is
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // If it's a local URL (starts with /uploads), prepend the backend URL
    if (imageUrl.startsWith('/uploads')) {
      return `${backendConfig.baseUrl}${imageUrl}`;
    }
    
    return imageUrl;
  };

  // Handle image load errors
  const handleImageError = (sceneIndex, imageUrl) => {
    console.log(`Image failed to load for scene ${sceneIndex}:`, imageUrl);
    
    // Don't retry local URLs (they should always work)
    if (imageUrl && imageUrl.startsWith('/uploads')) {
      console.log('Local URL failed to load, marking as permanently failed:', imageUrl);
      setImageLoadErrors(prev => ({
        ...prev,
        [sceneIndex]: {
          url: imageUrl,
          timestamp: Date.now(),
          error: 'Local image failed to load',
          permanentlyFailed: true
        }
      }));
      return;
    }
    
    // Check if this is a retry attempt
    const existingError = imageLoadErrors[sceneIndex];
    const retryCount = existingError?.retryCount || 0;
    
    if (retryCount < 2) {
      // Retry loading the image after a short delay
      console.log(`Retrying image load for scene ${sceneIndex}, attempt ${retryCount + 1}`);
      setTimeout(() => {
        // Force a re-render by updating the image source
        const updatedScenes = [...scenes];
        if (updatedScenes[sceneIndex]) {
          updatedScenes[sceneIndex].imageUrl = imageUrl;
          setScenes(updatedScenes);
        }
      }, 1000 * (retryCount + 1)); // Increasing delay: 1s, 2s
      
      // Update retry count
      setImageLoadErrors(prev => ({
        ...prev,
        [sceneIndex]: {
          ...existingError,
          retryCount: retryCount + 1,
          url: imageUrl,
          timestamp: Date.now(),
          error: 'Image URL expired or inaccessible'
        }
      }));
    } else {
      // Mark as permanently failed after retries
      setImageLoadErrors(prev => ({
        ...prev,
        [sceneIndex]: {
          url: imageUrl,
          timestamp: Date.now(),
          error: 'Image URL expired or inaccessible',
          retryCount: retryCount,
          permanentlyFailed: true
        }
      }));
    }
  };

  // Handle successful image loads
  const handleImageLoad = (sceneIndex) => {
    console.log(`Image loaded successfully for scene ${sceneIndex}`);
    setImageLoadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[sceneIndex];
      return newErrors;
    });
  };

  // Regenerate artwork for a specific scene
  const regenerateArtwork = async (sceneIndex) => {
    if (sceneIndex === 0) {
      setModal({
        isOpen: true,
        title: "🎨 Scene 1 Artwork",
        message: "Scene 1 uses your original artwork. If you need new artwork, please upload a new image! ✨",
        type: 'info'
      });
      return;
    }

    const scene = scenes[sceneIndex];
    if (!scene) {
      setModal({
        isOpen: true,
        title: "Scene Not Found",
        message: `Scene ${sceneIndex + 1} doesn't exist. Please create a new scene first.`,
        type: 'error'
      });
      return;
    }

    // Get the previous scene's artwork as input
    const previousSceneIndex = sceneIndex - 1;
    const previousScene = scenes[previousSceneIndex];
    if (!previousScene || !previousScene.imageUrl) {
      setModal({
        isOpen: true,
        title: "Missing Previous Artwork",
        message: `Scene ${previousSceneIndex + 1} needs artwork before regenerating artwork for Scene ${sceneIndex + 1}. Please add artwork to the previous scene first.`,
        type: 'warning'
      });
      return;
    }

    // Use artwork details or scene description for regeneration
    const promptForImage = scene.artworkDetails || scene.description || '';
    if (!promptForImage.trim()) {
      setModal({
        isOpen: true,
        title: "Missing Scene Details",
        message: "Please write artwork details or scene description before regenerating artwork.",
        type: 'warning'
      });
      return;
    }

    setGeneratingArtwork(true);
    setError(null);

    try {
      console.log('=== REGENERATING ARTWORK ===');
      console.log('Regenerating artwork for scene', sceneIndex + 1);
      console.log('Previous scene artwork URL:', previousScene.imageUrl);
      console.log('Artwork details prompt:', promptForImage);

      // Test API connectivity first
      const isAPIAvailable = await testAPIConnectivity();
      if (!isAPIAvailable) {
        throw new Error('Backend API is not accessible. Please check if the backend server is running.');
      }

      const requestBody = {
        previousImageUrl: getFullImageUrl(previousScene.imageUrl),
        scenePrompt: promptForImage
      };

      const response = await fetch(API_ENDPOINTS.generateStoryImage, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        let backendError = 'Failed to regenerate artwork for this scene.';
        try {
          const errData = await response.json();
          backendError = errData.error || backendError;
        } catch {}
        throw new Error(backendError);
      }

      const data = await response.json();
      console.log('Artwork regeneration response:', data);

      if (!data.generatedImageUrl) {
        throw new Error('No image returned from backend.');
      }

      // Update the scene's imageUrl
      const updatedScenes = [...scenes];
      if (updatedScenes[sceneIndex]) {
        updatedScenes[sceneIndex].imageUrl = data.generatedImageUrl;
        setScenes(updatedScenes);
        
        // Clear the image load error for this scene
        setImageLoadErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[sceneIndex];
          return newErrors;
        });

        // Force a re-render by updating current scene if this is the active scene
        if (sceneIndex === currentSceneIndex) {
          setCurrentScene(updatedScenes[sceneIndex]);
        }

        setModal({
          isOpen: true,
          title: "🎨 Artwork Regenerated!",
          message: "Your artwork has been successfully regenerated with a fresh URL! ✨",
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Artwork regeneration error:', error);
      setError(error.message);
      setModal({
        isOpen: true,
        title: "❌ Regeneration Failed",
        message: `Failed to regenerate artwork: ${error.message}`,
        type: 'error'
      });
    } finally {
      setGeneratingArtwork(false);
    }
  };

  // Add state for upload loading
  const [uploadingArtwork, setUploadingArtwork] = useState(false);

  // Add state to track if tagging is required after upload
  const [requireTaggingAfterUpload, setRequireTaggingAfterUpload] = useState(false);

  // Add state for pending upload file and tags
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [pendingUploadTags, setPendingUploadTags] = useState([]);

  // Add state to track when tagged characters are updated
  const [taggedCharactersUpdateCount, setTaggedCharactersUpdateCount] = useState(0);
  const [taggingComplete, setTaggingComplete] = useState(true);
  const [canOpenGenerateModal, setCanOpenGenerateModal] = useState(true);

  // Force modal to re-render when tagged characters are updated
  useEffect(() => {
    if (taggedCharactersUpdateCount > 0) {
      console.log('Tagged characters updated, forcing modal re-render');
      // Close and reopen the modal to get fresh data
      if (showGenerateArtworkModal) {
        setShowGenerateArtworkModal(false);
        setTimeout(() => {
          setShowGenerateArtworkModal(true);
        }, 100);
      }
    }
  }, [taggedCharactersUpdateCount]);

  // Change handleUploadArtwork to open tagging modal instead of uploading immediately
  const handleUploadArtwork = (event) => {
    console.log('=== UPLOAD ARTWORK CLICKED ===');
    console.log('event:', event);
    console.log('event.target.files:', event.target.files);
    
    const file = event.target.files && event.target.files[0];
    console.log('selected file:', file);
    
    if (!file) {
      console.log('No file selected');
      return;
    }
    
    console.log('Setting pending upload file and opening artwork tagger');
    setPendingUploadFile(file);
    setShowArtworkTagger(true);
    console.log('showArtworkTagger should now be true');
  };

  // New function to handle upload after tagging
  const handleUploadAfterTagging = async (tags, tagData) => {
    if (!pendingUploadFile) return;
    setUploadingArtwork(true);
    try {
      // 1. Upload artwork
      const formData = new FormData();
      formData.append('artwork', pendingUploadFile);
      formData.append('sceneIndex', currentSceneIndex);
      formData.append('storyId', storyId || '');
      formData.append('tags', JSON.stringify(tags));
      formData.append('tagData', JSON.stringify(tagData)); // Append tagData

      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/upload-artwork`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to upload artwork');
      const data = await response.json();
      if (!data.imageUrl) throw new Error('No image URL returned');

      // 2. Now call /api/process-tags with the new imageUrl and tags
      const processTagsPayload = {
        ...tagData,
        artworkUrl: data.imageUrl, // Use the uploaded image URL
      };
      console.log('Calling /api/process-tags with:', processTagsPayload);
      const processTagsResponse = await fetch(`${API_ENDPOINTS.backendBase}/api/process-tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(processTagsPayload),
      });
      if (!processTagsResponse.ok) throw new Error('Failed to process tags');
      const processTagsResult = await processTagsResponse.json();
      console.log('Processed tags result:', processTagsResult);
      const processedTags = processTagsResult.processedTags || [];
      // Always use processedTags (with imageUrl) for taggedCharacters
      const updatedScenes = [...scenes];
      if (updatedScenes[currentSceneIndex]) {
        updatedScenes[currentSceneIndex].imageUrl = data.imageUrl;
        updatedScenes[currentSceneIndex].taggedCharacters = processedTags.length > 0 ? processedTags : tags;
        setScenes(updatedScenes);
      }
      setModal({
        isOpen: true,
        title: '🎨 Artwork Uploaded & Tagged!',
        message: 'Your artwork and tags have been uploaded and set for this scene.',
        type: 'success',
      });
      setPendingUploadFile(null);
      setPendingUploadTags([]);
      // Do NOT close the modal here
    } catch (error) {
      setModal({
        isOpen: true,
        title: '❌ Upload Failed',
        message: error.message || 'Failed to upload artwork.',
        type: 'error',
      });
    } finally {
      setUploadingArtwork(false);
    }
  };

  // New function to handle existing artwork tagging with backend processing
  const handleExistingArtworkTagging = async (tags, tagData) => {
    console.log('=== HANDLE EXISTING ARTWORK TAGGING CALLED ===');
    console.log('Function called at:', new Date().toISOString());
    console.log('Tags received:', tags);
    console.log('Tag data received:', tagData);
    setUploadingArtwork(true);
    setTaggingComplete(false); // Set tagging as in progress
    try {
      // Send tag data to backend for image cropping and storage
      console.log('Sending POST to /api/process-tags with body:', tagData);
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/process-tags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tagData),
      });
      console.log('POST to /api/process-tags completed, response:', response);
      if (!response.ok) {
        throw new Error('Failed to process tags in backend');
      }
      const result = await response.json();
      console.log('Backend processing result:', result);
      const processedTags = result.processedTags || [];
      console.log('Backend processedTags:', JSON.stringify(processedTags, null, 2));
      // Update the current scene with the processed tags (with imageUrl)
      const updatedScenes = [...scenes];
      if (updatedScenes[currentSceneIndex]) {
        if (processedTags.length > 0) {
          updatedScenes[currentSceneIndex].taggedCharacters = processedTags;
          console.log('Updated scene taggedCharacters (from processedTags):', JSON.stringify(processedTags, null, 2));
        } else {
          updatedScenes[currentSceneIndex].taggedCharacters = tags;
          console.log('Updated scene taggedCharacters (fallback to tags):', JSON.stringify(tags, null, 2));
        }
        setScenes(updatedScenes);
        setTimeout(() => {
          console.log('After setScenes, updatedScenes:', JSON.stringify(updatedScenes, null, 2));
          console.log('After setScenes, updatedScenes[currentSceneIndex].taggedCharacters:', JSON.stringify(updatedScenes[currentSceneIndex].taggedCharacters, null, 2));
        }, 1000);
        setTimeout(() => {
          console.log('After setScenes, scenes state:', JSON.stringify(scenes, null, 2));
          console.log('After setScenes, scenes[currentSceneIndex].taggedCharacters:', JSON.stringify(scenes[currentSceneIndex]?.taggedCharacters, null, 2));
        }, 1500);
        // Force a re-render of the GenerateArtworkModal by updating the counter
        setTaggedCharactersUpdateCount(prev => prev + 1);
        console.log('Tagged characters update counter incremented');
        // Add a small delay to ensure state updates complete
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log('State update delay completed');
      }
      setModal({
        isOpen: true,
        title: '✅ Tags Processed Successfully!',
        message: 'Your tags have been processed and saved with cropped images.',
        type: 'success',
      });
    } catch (error) {
      console.error('Error processing tags:', error);
      setModal({
        isOpen: true,
        title: '❌ Tag Processing Failed',
        message: error.message || 'Failed to process tags in backend.',
        type: 'error',
      });
    } finally {
      setUploadingArtwork(false);
      setTaggingComplete(true); // Set tagging as complete
    }
  };

  // Show loading state while checking authentication
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-white"><div className="relative z-10 p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm border-2 border-amber-200 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center justify-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                <p className="text-amber-800">Loading your creative space...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white"><div className="relative z-10 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={handleBack}
              className="flex items-center text-gray-900 hover:text-amber-200 mb-6 "
          >
            <FaArrowLeft className="mr-2" />
            Back
          </button>
          <div className="bg-white/80 backdrop-blur-sm border-2 border-amber-200 rounded-2xl p-8 shadow-lg">
            <div className="flex items-center justify-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              <p className="text-amber-800">Creating your magical story...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white"><div className="relative z-10 p-3 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Top Bar: Back (left) and My Art Stories (right) */}
        <div className="flex justify-between items-center mb-4">
          <BackButton className="mb-0" />
          <button
            onClick={() => navigate('/my-art-stories')}
              className="bg-gradient-to-r from-amber-400 to-amber-200 text-gray-900 font-bold py-2 px-4 sm:px-6 rounded-xl shadow hover:from-amber-500 hover:to-amber-300 transition-all duration-300 text-sm sm:text-base"
          >
            My Art Stories
          </button>
        </div>
        {/* Top Bar Buttons */}
        {(scenes.length > 0 || firstSceneGenerated) && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button className="bg-gradient-to-r from-amber-500 to-amber-400 text-gray-900 font-semibold py-2 px-3 sm:px-4 rounded-xl shadow hover:shadow-xl transition-all duration-300 text-sm sm:text-base">Publish Story</button>
            <button
              onClick={handleSaveStory}
                className="bg-gradient-to-r from-amber-400 to-amber-300 text-gray-900 font-semibold py-2 px-3 sm:px-4 rounded-xl shadow hover:shadow-xl transition-all duration-300 text-sm sm:text-base"
            >
              Save Story
            </button>
            <button
                className="bg-white/80 border border-amber-200 text-amber-700 font-semibold py-2 px-3 sm:px-4 rounded-xl shadow hover:bg-amber-100 transition-all duration-300 text-sm sm:text-base"
              onClick={() => {
                navigate('/view-book', {
                  state: {
                    storyTitle,
                    authorName,
                    bookCover,
                    scenes,
                    originalArtwork
                  }
                });
              }}
            >
              View My Book
            </button>
          </div>
        </div>
        )}
        {/* Error Message */}
        {error && (
            <div className="mb-4 p-3 sm:p-4 bg-red-50/80 backdrop-blur-sm border-2 border-red-200 rounded-xl text-red-700 text-sm sm:text-base">
            <p className="font-semibold">Oops! Something went wrong:</p>
            <p>{error}</p>
            <button
              onClick={handleBack}
                className="mt-3 sm:mt-4 bg-gradient-to-r from-amber-400 to-amber-300 text-gray-900 font-semibold py-2 px-3 sm:px-4 rounded-xl shadow hover:shadow-xl transition-all duration-300 text-sm sm:text-base"
            >
              Go Back
            </button>
          </div>
        )}
        {/* Character Management Buttons */}
        {(scenes.length > 0 || firstSceneGenerated) && (
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {/* Removed duplicate Manage Characters button */}
          </div>
        )}
          
          {/* Buttons row above title/content box - only Character Manager remains */}
          {(scenes.length > 0 || firstSceneGenerated) && (
            <div className="mb-4"></div>
          )}
        </div>

        {/* Image Load Errors Summary */}
        {Object.keys(imageLoadErrors).filter(key => imageLoadErrors[key]?.permanentlyFailed).length > 0 && (
          <div className="mb-4 p-3 sm:p-4 bg-orange-50/80 backdrop-blur-sm border-2 border-orange-200 rounded-xl text-orange-700 text-sm sm:text-base">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">⚠️</span>
              <p className="font-semibold">Some artwork URLs have expired</p>
            </div>
            <p className="mb-2">
              {Object.keys(imageLoadErrors).filter(key => imageLoadErrors[key]?.permanentlyFailed).length === 1 
                ? `Scene ${parseInt(Object.keys(imageLoadErrors).filter(key => imageLoadErrors[key]?.permanentlyFailed)[0]) + 1} has an expired artwork URL.`
                : `${Object.keys(imageLoadErrors).filter(key => imageLoadErrors[key]?.permanentlyFailed).length} scenes have expired artwork URLs.`
              }
            </p>
            <p className="text-xs mb-3">
              This happens when generated image URLs expire. You can regenerate artwork for each scene using the buttons in the artwork area.
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.keys(imageLoadErrors)
                .filter(key => imageLoadErrors[key]?.permanentlyFailed)
                .map(sceneIndex => (
                <button
                  key={sceneIndex}
                  onClick={() => regenerateArtwork(parseInt(sceneIndex))}
                  disabled={generatingArtwork}
                  className={`px-3 py-1 rounded-lg font-semibold text-xs transition-all duration-300 ${
                    generatingArtwork
                      ? 'bg-gray-400 text-gray-900 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-500 to-orange-400 text-gray-900 hover:from-orange-600 hover:to-orange-500'
                  }`}
                >
                  {generatingArtwork ? 'Regenerating...' : `Regenerate Scene ${parseInt(sceneIndex) + 1}`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Two-Column Layout */}
        {shouldShowNewStoryFlow ? (
          // Intro/landing page: stack artwork and welcome message vertically, centered
          <div className="flex flex-col items-center justify-center min-h-[70vh] gap-8">
            <div className="bg-white/80 rounded-2xl border-4 border-amber-200 shadow-lg p-4 sm:p-8 flex flex-col items-center max-w-lg w-full">
              <img
                src={originalArtwork}
                alt="Original Artwork"
                className="w-full max-w-xs h-80 object-cover rounded-xl border-4 border-amber-200 shadow mb-6"
                style={{ background: '#fff' }}
              />
              <div className="text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-amber-800 mb-4 ">Hello fellow storyteller,<br />let's begin the story adventure with Artmosphere!</h2>
                        <button
                  className="bg-amber-400 hover:bg-amber-500 text-gray-900 font-semibold py-3 px-6 rounded-xl shadow-lg text-lg mb-2"
                  onClick={() => setShowArtworkInfoModal(true)}
                >
                  Tell Artmosphere about this artwork
                        </button>
                <div className="text-amber-700 text-base mt-2">Click to answer questions about your artwork and start your magical story adventure!</div>
                      </div>
                      </div>
                      </div>
        ) : (
          // Two-column layout: artwork left, notebook right
          <div className="flex flex-col lg:flex-row items-start lg:items-stretch gap-0 lg:gap-16 overflow-y-auto max-h-screen min-h-screen py-12">
            {/* Left: Artwork column */}
            <div className="w-full lg:w-[480px] flex-shrink-0 flex flex-col items-center justify-center pr-0 py-8 lg:py-0">
              <div className="relative w-full flex flex-col items-center">
                {/* Add Tag button absolutely positioned at top left of artwork */}
                {!shouldShowNewStoryFlow && (
                      <button
                    className="absolute top-3 left-3 z-10 bg-amber-400 hover:bg-amber-500 text-gray-900 font-semibold py-2 px-5 rounded-xl shadow-lg text-base transition-all duration-300"
                    style={{ fontFamily: '"Baloo 2", "Comic Sans MS", cursive' }}
                    onClick={() => setShowArtworkTagger(true)}
                  >
                    + Add Tag
                      </button>
                )}
                <ArtworkDisplay
                  currentSceneIndex={currentSceneIndex}
                  scenes={scenes}
                  originalArtwork={originalArtwork}
                  getFullImageUrl={artworkHandler.getFullImageUrl}
                  showCharacterTags={showCharacterTags}
                  handleImageError={handleImageError}
                  handleImageLoad={handleImageLoad}
                  imageLoadErrors={imageLoadErrors}
                  onTagCharacters={() => setShowArtworkTagger(true)}
                  onRefineArtwork={() => {
                    console.log('=== REFINE ARTWORK BUTTON CLICKED ===');
                    console.log('Current scene index:', currentSceneIndex);
                    console.log('Scene imageUrl:', scenes[currentSceneIndex]?.imageUrl);
                    console.log('Original artwork:', originalArtwork);
                    console.log('Pending upload file:', pendingUploadFile);
                    console.log('Pending upload file name:', pendingUploadFile?.name);
                    
                    // Get the full image URL for refinement
                    let imageUrl = artworkHandler.getFullImageUrl(scenes[currentSceneIndex]?.imageUrl || originalArtwork);
                    
                    // Check if we got a valid URL
                    if (!imageUrl) {
                      console.error('No valid image URL found for refinement');
                      setModal({
                        isOpen: true,
                        title: "❌ Cannot Refine Artwork",
                        message: "The current artwork is not saved yet. Please upload and save your artwork before refining it.",
                        type: 'error'
                      });
                      return;
                    }
                    
                    // Additional check for blob URLs (should be caught by getFullImageUrl, but just in case)
                    if (imageUrl.startsWith('blob:')) {
                      console.error('Blob URL still detected after getFullImageUrl');
                      setModal({
                        isOpen: true,
                        title: "❌ Cannot Refine Artwork",
                        message: "The current artwork is not saved yet. Please upload and save your artwork before refining it.",
                        type: 'error'
                      });
                      return;
                    }
                    
                    console.log('Final image URL for refinement:', imageUrl);
                    console.log('Tagged characters:', scenes[currentSceneIndex]?.taggedCharacters || []);
                    openRefineArtworkModal(
                      imageUrl,
                      scenes[currentSceneIndex]?.taggedCharacters || []
                    );
                  }}
                  onTellAtmosphereAboutArtwork={() => {
                    console.log('=== TELL ATMOSPHERE BUTTON CLICKED ===');
                    console.log('showArtworkInfoModal before:', showArtworkInfoModal);
                    console.log('originalArtwork:', originalArtwork);
                    console.log('artworkHandler.getFullImageUrl(originalArtwork):', artworkHandler.getFullImageUrl(originalArtwork));
                    setShowArtworkInfoModal(true);
                    console.log('showArtworkInfoModal after setState:', true);
                  }}
                />
              </div>
              {/* Upload Artwork and Generate Artwork Buttons below artwork (no Tag Characters button here) */}
              {!shouldShowNewStoryFlow && (
                <div className="flex flex-row gap-2 mt-3 justify-center mb-0">
                    <button
                    className={`flex-1 min-w-[120px] max-w-[180px] text-sm font-semibold py-2 px-3 rounded-xl shadow transition-all duration-300 ${
                      'bg-gradient-to-r from-green-500 to-green-400 hover:from-green-600 hover:to-green-500 text-gray-900'
                    }`}
                    onClick={() => {
                      // Create a temporary file input and trigger it
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.accept = 'image/*';
                      fileInput.onchange = (event) => {
                        handleUploadArtwork(event);
                      };
                      fileInput.click();
                    }}
                  >
                    📤 Upload Artwork
                    </button>
                    <button
                    className={`flex-1 min-w-[120px] max-w-[180px] text-sm font-semibold py-2 px-3 rounded-xl shadow transition-all duration-300 ${
                      taggingComplete && canOpenGenerateModal
                        ? 'bg-gradient-to-r from-amber-600 to-amber-400 hover:from-amber-700 hover:to-amber-500 text-gray-900' 
                        : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    }`}
                    onClick={() => {
                      setCanOpenGenerateModal(false);
                      setTimeout(() => {
                        setShowGenerateArtworkModal(true);
                      }, 1000);
                      setTimeout(() => setCanOpenGenerateModal(true), 2000);
                    }}
                    disabled={!taggingComplete || !canOpenGenerateModal}
                  >
                    🖼️ Generate Artwork
                  </button>
                </div>
            )}
          </div>
            {/* Right: Notebook column */}
            <div className="flex-1 min-w-0 max-w-7xl mx-auto lg:mx-0 flex flex-col justify-center items-center py-8 lg:py-0 lg:ml-32">
              <div className="w-full border-4 border-amber-200 rounded-2xl flex flex-col" style={{ fontFamily: '"Baloo 2", "Comic Sans MS", cursive' }}>
                {/* Story Title Box (amber background) */}
                <div className="w-full bg-amber-50">
                  {sceneManager.renderStoryTitle && sceneManager.renderStoryTitle()}
        </div>
                {/* Scene Content Box (amber background) */}
                <div className="w-full bg-amber-50" style={{ minHeight: '320px' }}>
                  {sceneManager.renderStoryContent && sceneManager.renderStoryContent()}
      </div>
              </div>
                </div>
              </div>
        )}
          </div>

      {/* Custom Modal */}
      <CustomModal
        isOpen={modal.isOpen}
        onClose={() => setModal({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
      />

      {/* Character Manager Modal */}
      {showCharacterManager && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative">
            <button
              className="absolute top-3 right-4 text-2xl text-gray-400 hover:text-gray-700"
              onClick={() => setShowCharacterManager(false)}
              title="Close"
            >
              ×
            </button>
            <CharacterManager
              storyId={storyId}
              onCharacterUpdate={handleCharacterUpdate}
              initialCharacters={characters}
            />
          </div>
        </div>
      )}

      {/* Scene Creator Modal */}
      {showSceneCreator && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative">
            <button
              className="absolute top-3 right-4 text-2xl text-gray-400 hover:text-gray-700"
              onClick={() => setShowSceneCreator(false)}
              title="Close"
            >
              ×
            </button>
            <SceneCreator
              storyId={storyId}
              sceneNumber={currentSceneNumber}
              previousImageUrl={scenes.length > 0 ? scenes[scenes.length - 1]?.imageUrl : originalArtwork}
              onSceneGenerated={handleSceneGenerated}
              existingCharacters={characters}
            />
          </div>
        </div>
      )}

      {/* Artwork Info Modal */}
      <ArtworkInfoModal
        isOpen={showArtworkInfoModal}
        onClose={() => setShowArtworkInfoModal(false)}
        artworkUrl={artworkHandler.getFullImageUrl(originalArtwork)}
        onSceneGenerated={(newScene) => {
          console.log('=== SCENE GENERATION ===');
          console.log('Original taggedCharacters state:', taggedCharacters);
          console.log('New scene data:', newScene);
          
          // Use the tagged characters from the new scene if available, otherwise use local state
          const sceneTaggedCharacters = newScene.taggedCharacters || taggedCharacters;
          
          // Include tagged characters in the scene data with their positions
          const sceneWithTags = {
            ...newScene,
            taggedCharacters: sceneTaggedCharacters.map(tag => ({
              ...tag,
              x: tag.x || 20, // Ensure x coordinate exists
              y: tag.y || 20, // Ensure y coordinate exists
              isPlaced: tag.isPlaced || true // Ensure isPlaced flag exists
            }))
          };
          console.log('Scene with tags:', sceneWithTags);
          console.log('Final tagged characters in scene:', sceneWithTags.taggedCharacters);
          setScenes([sceneWithTags]);
          setCurrentScene(sceneWithTags);
          setCurrentSceneContent(newScene.description || '');
          setCurrentSceneIndex(0);
          setCharacters(newScene.characters || []);
          setShowArtworkInfoModal(false);
          setFirstSceneGenerated(true);
          setModal({
            isOpen: true,
            title: '🎭 Scene Created!',
            message: 'Your first scene has been generated! You can now view your Character Library or create the next scene.',
            type: 'success',
          });
        }}
        currentUser={currentUser}
        onTaggedCharactersSaved={(tags) => {
          console.log('=== TAGGED CHARACTERS SAVED FROM MODAL ===');
          console.log('Tags received:', tags);
          console.log('Tag positions:', tags.map(tag => ({ name: tag.name, x: tag.x, y: tag.y, isPlaced: tag.isPlaced })));
          setTaggedCharacters(tags);
        }}
      />

      {/* ArtworkTagger Modal */}
      {showArtworkTagger && (
        // Gather all unique characters from all scenes
        (() => {
          const allCharacters = Array.from(
            new Map(
              scenes
                .flatMap(scene => (scene.characters || []))
                .filter(c => c && c.name)
                .map(c => [c.name, c])
            ).values()
          );
          return (
        <ArtworkTagger
          key={taggedCharactersUpdateCount} // Add key to force re-render
          artworkUrl={pendingUploadFile ? URL.createObjectURL(pendingUploadFile) : artworkHandler.getFullImageUrl(scenes[currentSceneIndex]?.imageUrl || originalArtwork)}
          initialTags={pendingUploadFile ? [] : (scenes[currentSceneIndex]?.taggedCharacters || [])}
          onCharactersTagged={async (tags, tagData) => {
            console.log('=== ARTWORKTAGGER CALLBACK TRIGGERED ===');
            console.log('Callback triggered at:', new Date().toISOString());
            console.log('Tags from ArtworkTagger:', tags);
            console.log('Tag data from ArtworkTagger:', tagData);
            console.log('=== HANDLING TAG SAVE FROM ARTWORKTAGGER ===');
            console.log('Tags:', tags);
            console.log('Tag data for backend:', tagData);
            
            if (pendingUploadFile) {
              // For new uploads, include the file in the backend processing
              console.log('Processing as new upload with file');
              await handleUploadAfterTagging(tags, tagData);
            } else {
              // For existing artwork, send tag data to backend for processing
              console.log('Processing as existing artwork');
              await handleExistingArtworkTagging(tags, tagData);
            }
          }}
          initialCharacters={allCharacters.length > 0 ? allCharacters : artworkCharacters.filter(c => c.name.trim())}
          onClose={() => {
            // Only allow closing if not uploading
            if (!uploadingArtwork) {
            setShowArtworkTagger(false);
              setPendingUploadFile(null);
              setPendingUploadTags([]);
            }
          }}
          storyId={storyId}
          currentUser={currentUser}
          hideGenerateSceneButton={true}
          onRefineArtwork={(artworkUrl, taggedChars) => {
            setCurrentArtworkForRefinement(artworkUrl);
            setCurrentTaggedCharactersForRefinement(taggedChars);
            setShowArtworkTagger(false);
            setShowRefineArtworkModal(true);
          }}
        />
          );
        })()
      )}

      {/* RefineArtworkModal */}
      {showRefineArtworkModal && (
        <RefineArtworkModal
          isOpen={showRefineArtworkModal}
          onClose={() => setShowRefineArtworkModal(false)}
          artworkUrl={currentArtworkForRefinement}
          currentUser={currentUser}
          taggedCharacters={currentTaggedCharactersForRefinement}
          onArtworkRefined={handleArtworkRefined}
          storyId={storyId}
        />
      )}

      {/* Generate Artwork Modal */}
      {showGenerateArtworkModal && !shouldShowNewStoryFlow && (
        <GenerateArtworkModal
          key={taggedCharactersUpdateCount} // Add key to force re-render
          isOpen={showGenerateArtworkModal}
          onClose={() => setShowGenerateArtworkModal(false)}
          allCharacters={(() => {
            const characters = Array.from(
              new Map(
                [
                  // Include all tagged characters from all scenes
                  ...scenes.flatMap(scene => (scene.taggedCharacters || [])),
                  // Also include any characters from the current scene (even if not tagged yet)
                  ...(scenes[currentSceneIndex]?.characters || [])
                ]
                  .filter(c => c && c.name)
                  .map(c => [c.id || c.name, c])
              ).values()
            );
            console.log('=== PASSING CHARACTERS TO MODAL ===');
            console.log('scenes:', scenes);
            console.log('currentSceneIndex:', currentSceneIndex);
            console.log('currentScene taggedCharacters:', scenes[currentSceneIndex]?.taggedCharacters);
            console.log('allCharacters being passed:', characters);
            console.log('allCharacters details:', characters.map(c => ({
              id: c.id,
              name: c.name,
              imageUrl: c.imageUrl,
              hasImageUrl: !!c.imageUrl,
              type: c.type
            })));
            return characters;
          })()}
          storyId={storyId}
          currentUser={currentUser}
          currentSceneIndex={currentSceneIndex}
          onArtworkGenerated={(imageUrl, selectedCharacters, sceneDescription) => {
            // Update the current scene with the new artwork and characters
            const updatedScenes = [...scenes];
            if (updatedScenes[currentSceneIndex]) {
              updatedScenes[currentSceneIndex].imageUrl = imageUrl;
              updatedScenes[currentSceneIndex].taggedCharacters = selectedCharacters;
              updatedScenes[currentSceneIndex].artworkDetails = sceneDescription;
              setScenes(updatedScenes);
              setCurrentScene(updatedScenes[currentSceneIndex]);
            }
            setShowGenerateArtworkModal(false);
          }}
        />
      )}
    </div>
  );}
